/**
 * In-memory sharing of one load between the cards of a page: Ambientes and
 * Links read the same Argo CD state, Sobre and Rotas the same gateway APIs.
 * Results live only in this module's memory for a short time; nothing is
 * written to any browser storage.
 */

const TTL_MS = 30_000;

type Entry = { expires: number; promise: Promise<unknown> };

const entries = new Map<string, Entry>();

/** The pending or recent result of `load` for `key`, or a new load. */
export function sharedLoad<T>(
  key: string,
  load: () => Promise<T>,
  now: () => number = Date.now,
): Promise<T> {
  const cached = entries.get(key);
  if (cached && cached.expires > now()) {
    return cached.promise as Promise<T>;
  }
  const promise = load();
  entries.set(key, { expires: now() + TTL_MS, promise });
  // A failed load is not kept: the next card or visit tries again.
  promise.catch(() => {
    if (entries.get(key)?.promise === promise) {
      entries.delete(key);
    }
  });
  return promise;
}

/** Forgets every shared result (tests, and a fresh start). */
export function clearSharedLoads() {
  entries.clear();
}
